"use client"

import { motion } from "framer-motion"
import { Code2, Layers, Zap } from "lucide-react"
import { aboutHighlights, statCards } from "@/features/home/data"
import { fadeLeft, fadeRight, staggerContainer, staggerItem, sectionLabel, fadeUp } from "@/lib/motion"

const iconMap = { Code2, Layers, Zap }
type IconName = keyof typeof iconMap

const VP = { once: true, amount: 0.05 }
const workflowRows = [
  ["01", "analiz", "hedefler netleştirildi"],
  ["02", "tasarım", "akışlar sadeleştirildi"],
  ["03", "geliştirme", "temiz mimari kuruldu"],
  ["04", "test", "kritik senaryolar geçti"],
  ["05", "teslim", "ölçülebilir sonuç hazır"],
]

export function AboutSection() {
  return (
    <section id="hakkimizda" className="relative py-16 sm:py-24 lg:py-32" aria-label="Hakkımızda">
      <div className="brand-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
          >
            <motion.span
              variants={sectionLabel}
              className="text-primary font-medium text-sm uppercase tracking-wider inline-block"
            >
              Kimiz?
            </motion.span>

            <motion.h2
              variants={fadeLeft}
              className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-5 sm:mb-6 text-foreground"
            >
              Yazılımda <span className="text-gradient-brand">Mükemmellik</span>
            </motion.h2>

            <motion.p variants={fadeLeft} className="text-muted-foreground text-lg leading-relaxed mb-6">
              FiveLine Development olarak, 6 yılı aşkın süredir sektörün en zorlu projelerinde
              çözüm ortağı oluyoruz. Modern teknolojilerle işletmelerin dijital dönüşümünde
              güvenilir teknoloji partneri olarak yanlarındayız.
            </motion.p>

            <motion.p variants={fadeLeft} className="text-muted-foreground text-lg leading-relaxed mb-8">
              Her projede kod kalitesinden kullanıcı deneyimine, güvenlikten performansa kadar
              her detayı titizlikle ele alıyoruz. Müşterilerimizin başarısı, bizim başarımızdır.
            </motion.p>

            <motion.div variants={staggerContainer} className="space-y-4 mb-10">
              {aboutHighlights.map((item) => {
                const Icon = iconMap[item.iconName as IconName]
                return (
                  <motion.div
                    key={item.text}
                    variants={staggerItem}
                    className="flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      {Icon ? <Icon className="w-5 h-5 text-primary" /> : null}
                    </div>
                    <span className="text-foreground">{item.text}</span>
                  </motion.div>
                )
              })}
            </motion.div>

            <motion.div
              variants={staggerContainer}
              className="grid grid-cols-2 gap-3 sm:gap-4"
            >
              {statCards.map((stat) => (
                <motion.div
                  key={stat.label}
                  variants={fadeUp}
                  className="glass hairline rounded-2xl p-4 text-center"
                >
                  <p className="font-[family-name:var(--font-heading)] text-2xl sm:text-3xl font-bold text-gradient-brand">
                    {stat.value}
                  </p>
                  <p className="text-muted-foreground text-xs sm:text-sm mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={VP}
            className="relative hidden lg:block"
            aria-hidden="true"
          >
            <div className="relative aspect-square max-w-sm sm:max-w-md lg:max-w-lg mx-auto">
              <div className="absolute inset-0 bg-primary/20 rounded-3xl blur-3xl" />
              <div className="relative glass hairline flex h-full flex-col overflow-hidden rounded-3xl bg-background/80 shadow-2xl shadow-primary/10">
                <div className="flex items-center justify-between border-b border-border/70 bg-secondary/35 px-5 py-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                    <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  </div>
                  <span className="font-[family-name:var(--font-mono)] text-xs text-muted-foreground">workflow.ts</span>
                </div>
                <div className="flex flex-1 flex-col justify-between p-5 font-[family-name:var(--font-mono)] text-xs text-muted-foreground">
                  <div className="space-y-3">
                    {workflowRows.map(([number, key, value]) => (
                      <div key={number} className="grid grid-cols-[2rem_6rem_1fr] items-center gap-3 rounded-xl border border-border/70 bg-card/70 px-3 py-2.5 shadow-sm">
                        <span className="text-muted-foreground/55">{number}</span>
                        <span className="text-primary">{key}</span>
                        <span className="text-muted-foreground">{value}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 rounded-xl border border-primary/20 bg-primary/10 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-primary">build:success</span>
                      <span className="text-primary/70">%100</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-background/80">
                      <div className="h-full w-full rounded-full bg-gradient-to-r from-primary to-brand-purple-light" />
                    </div>
                  </div>
                </div>

              </div>

              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-5 -right-5 z-10 glass hairline rounded-xl px-4 py-2"
              >
                <span className="text-sm text-primary font-medium">React</span>
              </motion.div>
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                className="absolute -bottom-5 -left-5 z-10 glass hairline rounded-xl px-4 py-2"
              >
                <span className="text-sm text-primary font-medium">Next.js</span>
              </motion.div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
                className="absolute top-1/2 -right-8 z-10 glass hairline rounded-xl px-4 py-2"
              >
                <span className="text-sm text-primary font-medium">Node.js</span>
              </motion.div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}
